import { extendZodWithOpenApi } from "@anatine/zod-openapi";
import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  CompanySchema,
  FileSchema,
  IndustrialProcessSchema,
  JobStatusSchema,
  LayoutSchema,
  MaterialGradeSchema,
  MaterialLayoutSchema,
  MaterialSchema,
  PartAnalysisSchema,
  PartSchema,
  ProcessStepSchema,
  ProductLineSchema,
  ProductSchema,
  QuoteRequestLineSchema,
  QuoteRequestSchema,
  UserSchema,
} from "../generated/prisma/zod";
import { FormatedFeatureSchema } from "../types";
import { ExportValidationErrorSchema } from "./exportContract";
extendZodWithOpenApi(z);

const c = initContract();
const ApiHeadersSchema = z.object({
  "x-api-key": z.string().describe("Your API key for authentication"),
  "x-company-id": z.string().describe("Your Fabera company ID"),
});

const PartForExternalSchema = PartSchema.pick({
  id: true,
  createdAt: true,
  updatedAt: true,
  companyId: true,
  rootParentId: true,
  parentId: true,
  type: true,
  internalReference: true,
  name: true,
}).extend({
  thickness: z.number().nullable().optional(),
  quantityInAssembly: z.number().int().nullable().openapi({
    description:
      "Number of this part in the assembly. Is mainly used for consummables in an assembly. For instance if a bolt is used 4 times in an assembly, this value would be 4",
  }),
  files: FileSchema.omit({
    hash: true,
  })
    .merge(
      PartAnalysisSchema.pick({
        urlGltf: true,
        urlPng: true,
        urlPdfAnnotated: true,
        urlPdfPreview: true,
        length: true,
        width: true,
        materialGrade: true,
        materialName: true,
        quantityInAssembly: true,
        previewStatus: true,
        gltfStatus: true,
        analyzeStatus: true,
      }),
    )
    .array(),
});

const QuoteRequestLineForExternalBasisSchema = QuoteRequestLineSchema.pick({
  id: true,
  createdAt: true,
  updatedAt: true,
  comment: true,
  quantity: true,
  predictionStatus: true,
  partId: true,
  quoteRequestId: true,
});

const ProcessStepForExternalSchema = ProcessStepSchema.pick({
  createdAt: true,
  updatedAt: true,
  id: true,
  fixedCostPerSeries: true,
  manTime: true,
  manHourlyPrice: true,
  industrialProcess: true,
  description: true,
  otherMachineTime: true,
  cycleTimeCost: true,
  machinePreparationTime: true,
  machinePreparationTimeCost: true,
  machineHourlyPrice: true,
}).extend({
  industrialProcess: IndustrialProcessSchema.openapi({
    description: "type of the process steps",
  }),
});

const MaterialLayoutForExternalSchema = MaterialLayoutSchema.omit({
  height: true,
}).extend({
  thickness: z.number().nullable().optional(),
});

const QuoteRequestLineForExternalSchema = QuoteRequestLineForExternalBasisSchema.extend({
  predictionStatus: JobStatusSchema.openapi({
    description:
      "The status of the prediction for the quote request line. Need to be PROCESSED before computing the quote request line cost",
  }).nullable(),
  part: PartForExternalSchema,
  processSteps: ProcessStepForExternalSchema.extend({
    productLines: ProductLineSchema.extend({
      product: ProductSchema,
    }).array(),
  }).array(),
  layout: LayoutSchema.pick({
    createdAt: true,
    updatedAt: true,
    id: true,
  })
    .extend({
      materialLayout: MaterialLayoutForExternalSchema.nullable(),
    })
    .nullable(),
});

const MaterialForExternalSchema = MaterialSchema.omit({
  internalId: true,
});

const ProductForExternalSchema = ProductSchema.pick({
  id: true,
  createdAt: true,
  updatedAt: true,
  name: true,
  price: true,
  companyId: true,
  type: true,
});

const ProductLineForExternalSchema = ProductLineSchema.pick({
  id: true,
  createdAt: true,
  updatedAt: true,
  isQuantityPerPart: true,
  quantity: true,
  productId: true,
}).extend({
  product: ProductForExternalSchema,
});

const QuoteRequestForExternalSchema = QuoteRequestSchema.pick({
  id: true,
  createdAt: true,
  updatedAt: true,
  companyId: true,
  isArchived: true,
  isImported: true,
}).extend({
  quoteRequestLines: QuoteRequestLineForExternalBasisSchema.array(),
});

const CompanyForExternalSchema = CompanySchema.pick({
  id: true,
  createdAt: true,
  updatedAt: true,
  name: true,
});

export const externalApiContract = c.router({
  createCompany: {
    method: "POST",
    path: "/api/companies",
    headers: ApiHeadersSchema.omit({ "x-company-id": true }),
    body: z.object({
      name: z.string().min(2).max(100).openapi({ description: "company name" }),
      users: UserSchema.pick({
        email: true,
        firstName: true,
        lastName: true,
      })
        .extend({
          password: z.string(),
        })
        .array()
        .optional(),
    }),
    responses: {
      201: CompanyForExternalSchema,
    },
    summary: "Create a company",
  },
  create: {
    method: "POST",
    path: "/api/quote-request-lines",
    headers: ApiHeadersSchema,
    body: z.object({
      quantity: z
        .number()
        .min(1)
        .optional()
        .openapi({ description: "quantity of part in the series" }),
      part: z.object({
        files: z.array(
          z.object({
            hash: z.string().optional().openapi({
              description:
                "a file hash (sha256 for instance) to setup a cache. Next time the same file will be downloaded, the same part will be reused, and the user won't have to wait for the aprt analyze.",
            }),
            name: z.string(),
            url: z.string().openapi({ description: "public step file url" }),
          }),
        ),
        materialGrade: MaterialGradeSchema.optional(),
      }),
    }),
    responses: {
      201: QuoteRequestLineForExternalSchema.omit({
        processSteps: true,
        layout: true,
      }),
    },
    summary: "Create a quote request line from a step file",
  },
  update: {
    method: "PATCH",
    path: "/api/quote-request-lines/:id",
    headers: ApiHeadersSchema,
    body: z.object({
      quantity: z
        .number()
        .min(1)
        .optional()
        .openapi({ description: "quantity of part in the series" }),
      materialGrade: MaterialGradeSchema.optional(),
    }),
    responses: {
      200: QuoteRequestLineForExternalSchema.omit({
        processSteps: true,
        layout: true,
      }),
    },
    summary: "Update a quote request line from a step file",
  },
  findOneQuoteRequest: {
    method: "GET",
    path: "/api/quote-requests/:id",
    headers: ApiHeadersSchema,
    responses: {
      200: QuoteRequestForExternalSchema,
    },
    summary: "Find a quote request by id",
  },
  exportQuoteRequest: {
    method: "GET",
    path: "/api/quote-requests/:id/exports",
    headers: ApiHeadersSchema,
    responses: {
      200: c.otherResponse({
        contentType: "application/zip",
        body: c.type<Buffer>(),
      }),
      204: z.void(),
      400: ExportValidationErrorSchema,
      403: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
    },
    summary:
      "Export the ERP file(s) of a quote request as a zip archive (csv or xls files, depending on the company's export template)",
  },
  findOneQuoteRequestLine: {
    method: "GET",
    path: "/api/quote-request-lines/:id",
    headers: ApiHeadersSchema,
    responses: {
      200: QuoteRequestLineForExternalSchema,
    },
    summary: "Find a quote request line by id",
  },
  findOnePart: {
    method: "GET",
    path: "/api/parts/:id",
    headers: ApiHeadersSchema,
    responses: {
      200: PartForExternalSchema,
    },
    summary: "Find a part by id",
  },
  predict: {
    method: "POST",
    path: "/api/quote-request-lines/:id/predict",
    headers: ApiHeadersSchema,
    body: z.object({
      shouldHandleAssembly: z.boolean().optional().default(true).openapi({
        description:
          "if `true` (default), then tall parts of the assembly will be predicted. If `false`, then only the assembly is predicted as a one part.",
      }),
    }),
    responses: {
      200: QuoteRequestLineForExternalSchema.omit({
        part: true,
        processSteps: true,
        layout: true,
      }).array(),
    },
    summary: "Predict the quote request line cost",
  },
  getAdditionalProcessSteps: {
    method: "GET",
    path: "/api/quote-request-lines/:id/additional-process-steps",
    headers: ApiHeadersSchema,
    query: z.object({
      materialGrade: MaterialGradeSchema,
      quantity: z.coerce.number().int().positive(),
    }),
    responses: {
      200: ProcessStepForExternalSchema.omit({
        id: true,
        createdAt: true,
        updatedAt: true,
        manTime: true,
        manHourlyPrice: true,
      })
        .extend({
          description: z.string().nullish(),
          machinePreparationTime: z.number().nullish(),
          machineHourlyPrice: z.number().nullish(),
          fixedCostPerSeries: z.number().nullish(),
          otherMachineTime: z.number().nullish(),
          cycleTimeCost: z.number().nullish(),
          machinePreparationTimeCost: z.number().nullish(),
        })
        .array(),
    },
    summary: "Get the additional process steps of a quote request line",
  },
  findFeatures: {
    method: "GET",
    path: "/api/parts/:id/features",
    headers: ApiHeadersSchema,
    responses: {
      200: FormatedFeatureSchema.array(),
    },
    summary: "Get the features of a part",
  },
  getModelVersion: {
    method: "GET",
    path: "/api/model-version",
    headers: ApiHeadersSchema,
    responses: {
      200: z.object({
        companyId: z.string().openapi({ description: "company id" }),
        version: z.string().openapi({ description: "model version" }),
      }),
    },
    summary: "Get the version of the model",
  },
  findMaterials: {
    method: "GET",
    path: "/api/materials",
    headers: ApiHeadersSchema,
    query: z.object({
      materialGrade: MaterialGradeSchema.optional().openapi({
        description: "Filter materials by material grade",
      }),
      limit: z.number().int().min(1).max(100).optional().default(50).openapi({
        description: "Maximum number of materials to return (1-100, default: 50)",
      }),
      offset: z.number().int().min(0).optional().default(0).openapi({
        description: "Number of materials to skip (default: 0)",
      }),
    }),
    responses: {
      200: z.object({
        materials: MaterialForExternalSchema.array(),
        total: z.number().min(0).openapi({
          description: "Total number of material suppliers matching the criteria",
        }),
      }),
    },
    summary: "Get available materials",
  },
  findMaterialSuppliers: {
    method: "GET",
    path: "/api/material-suppliers",
    headers: ApiHeadersSchema,
    query: z.object({
      materialGrade: MaterialGradeSchema.optional().openapi({
        description: "Filter materials by material grade",
      }),
      limit: z.number().int().min(1).max(100).optional().default(50).openapi({
        description: "Maximum number of materials to return (1-100, default: 50)",
      }),
      offset: z.number().int().min(0).optional().default(0).openapi({
        description: "Number of materials to skip (default: 0)",
      }),
    }),
    responses: {
      200: z.object({
        materials: MaterialForExternalSchema.array(),
        total: z.number().int().openapi({
          description: "Total number of materials matching the criteria",
        }),
      }),
    },
    summary: "Get available materials for this company",
  },
  findOneMaterial: {
    method: "GET",
    path: "/api/materials/:id",
    headers: ApiHeadersSchema,
    responses: {
      200: MaterialForExternalSchema,
    },
    summary: "Find a material by id",
  },
  findOneMaterialSupplier: {
    method: "GET",
    path: "/api/material-suppliers/:id",
    headers: ApiHeadersSchema,
    responses: {
      200: MaterialForExternalSchema,
    },
    summary: "Find a material by id",
  },
  findProducts: {
    method: "GET",
    path: "/api/products",
    headers: ApiHeadersSchema,
    query: z.object({
      limit: z.number().int().min(1).max(100).optional().default(50).openapi({
        description: "Maximum number of products to return (1-100, default: 50)",
      }),
      offset: z.number().int().min(0).optional().default(0).openapi({
        description: "Number of products to skip (default: 0)",
      }),
    }),
    responses: {
      200: z.object({
        products: ProductForExternalSchema.array(),
        total: z.number().min(0).openapi({
          description: "Total number of products matching the criteria",
        }),
      }),
    },
    summary: "Get available products",
  },
  findProductLines: {
    method: "GET",
    path: "/api/product-lines",
    headers: ApiHeadersSchema,
    query: z.object({
      productId: z.string().uuid().optional().openapi({
        description: "Filter product lines by product ID",
      }),
      limit: z.number().int().min(1).max(100).optional().default(50).openapi({
        description: "Maximum number of product lines to return (1-100, default: 50)",
      }),
      offset: z.number().int().min(0).optional().default(0).openapi({
        description: "Number of product lines to skip (default: 0)",
      }),
    }),
    responses: {
      200: z.object({
        productLines: ProductLineForExternalSchema.array(),
        total: z.number().int().openapi({
          description: "Total number of product lines matching the criteria",
        }),
      }),
    },
    summary: "Get available product lines",
  },
  findOneProduct: {
    method: "GET",
    path: "/api/products/:id",
    headers: ApiHeadersSchema,
    responses: {
      200: ProductForExternalSchema,
    },
    summary: "Find a product by id",
  },
  findOneProductLine: {
    method: "GET",
    path: "/api/product-lines/:id",
    headers: ApiHeadersSchema,
    responses: {
      200: ProductLineForExternalSchema,
    },
    summary: "Find a product line by id",
  },
  getWebhookSettings: {
    method: "GET",
    path: "/api/webhooks",
    headers: ApiHeadersSchema,
    responses: {
      200: z.object({
        webhookUrl: z.string().url().nullable(),
      }),
    },
    summary: "Get webhook configuration",
  },
  updateWebhookSettings: {
    method: "PUT",
    path: "/api/webhooks",
    headers: ApiHeadersSchema,
    body: z.object({
      webhookUrl: z.string().url().nullable().optional().openapi({
        description: "The URL to send webhook requests to",
      }),
    }),
    responses: {
      200: z.object({
        webhookUrl: z.string().url().nullable(),
        updatedAt: z.string(),
      }),
    },
    summary: "Update webhook configuration",
  },
});
